'use client'

import * as React from 'react'
import { RichText, RichTextProps } from 'basehub/react-rich-text'
import { flattenTipTapNodes } from '@basehub/tiptap-api-utils'

import { ButtonLink } from '~/sotano-kit/primitives/button'
import { getOffsetTop } from '~/lib/dom-utils'

import s from './toc.module.scss'

export type TocProps = RichTextProps & {
  title?: string
  currentSectionId?: string
  children: any
}

export const TocClient = ({
  title = 'In this article',
  blocks,
  children,
}: TocProps) => {
  const [currentSectionId, setCurrentSectionId] = React.useState('')
  const disabled = React.useRef(false)

  const setHighlightedSection = React.useCallback((sectionId: string) => {
    setCurrentSectionId((prevState) => {
      if (disabled.current) return prevState
      return sectionId
    })
  }, [])

  const headingListLength = React.useMemo(
    () =>
      flattenTipTapNodes(children).filter((item) =>
        item?.marks?.some((mark) => mark.type === 'link')
      ).length,
    [children]
  )
  const hasItems = headingListLength > 0

  React.useEffect(() => {
    if (!hasItems) {
      setCurrentSectionId('')
      return
    }

    const handleScroll = () => {
      let lastActive = ''
      const headingList = Array.from(
        document.querySelectorAll<HTMLHeadingElement>('h1, h2, h3, h4, h5, h6')
      ).filter((node) => node.id)

      // Is scrolled to the very bottom
      if (
        window.innerHeight + Math.round(window.scrollY) >=
          document.documentElement.scrollHeight &&
        !window.location.hash
      ) {
        lastActive = headingList[headingListLength - 1]?.id ?? ''
      } else {
        for (const heading of headingList) {
          const element = document.getElementById(heading.id)
          const offsetTop = getOffsetTop(element)

          if (
            element &&
            // Element is scrolled to the half of the screen bottom
            offsetTop <= window.innerHeight / 2 + Math.round(window.scrollY)
          ) {
            lastActive = heading.id
          } else {
            break
          }
        }
      }

      setHighlightedSection(lastActive)
    }

    handleScroll()
    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [hasItems, headingListLength, setHighlightedSection])

  return (
    <aside className={s.toc} data-hidden={!hasItems} aria-hidden={!hasItems}>
      {hasItems && (
        <>
          <p className="mb-1 flex items-center text-sm leading-normal">
            {title}
          </p>
          <RichText
            blocks={blocks}
            components={{
              a: ({ href, children }) => {
                if (!children) return <></>
                const childrenAsString = children.toString()
                return (
                  <ButtonLink
                    unstyled
                    focusStyled
                    href={href}
                    data-active={href === `#${currentSectionId}`}
                    onClick={() => {
                      setHighlightedSection(href.split('#')[1] as string)
                      // Sort of cooldown until the dom is done scrolling per the hash change (this way we avoid flickering in the toc)
                      disabled.current = true
                      setTimeout(() => (disabled.current = false), 400)
                    }}
                  >
                    <span>
                      {childrenAsString.length < 35
                        ? childrenAsString
                        : childrenAsString.slice(0, 35) + '...'}
                    </span>
                  </ButtonLink>
                )
              },
            }}
          >
            {children}
          </RichText>
        </>
      )}
    </aside>
  )
}
